import { drawCairoPropertyArt } from './cairoTileArt.ts'
import type { ReadyWorldId } from './worlds'

export const propertyArtPositions=[1,3,6,8,9,11,13,14,16,18,19,21,23,24,26,27,29,31,33,34,36,38] as const
const cairoMotifs=['minaret','lantern','dome','carved-window','gate','coffee-pot','bridge','palette','key','theatre','scales','scroll','fountain','lotus','sailboat','museum','astrolabe','palm','tram','coffee','balcony','pyramids'] as const
const urban=['townhouse','palette','coffee','market','skyscraper','theatre','bridge','tree','hotel','music','fountain','books','clock','flower','sailboat','museum','bicycle','arch','tram','bakery','balcony','garden']
const space=['rocket','telescope','biosphere','rover','satellite','solar','habitat','antenna','laboratory','airlock','oxygen','lander','orbit','radar','crystal','crater','astronaut','battery','greenhouse','cargo','beacon','planet']
// Each board has 22 different motifs; shared motifs are original reusable art,
// while the leading motifs and palette give each city's board a local identity.
const themes:Record<ReadyWorldId,readonly string[]>={
  'new-york':['skyscraper','townhouse','bridge','statue','taxi','theatre','music','gallery'],
  'las-vegas':['neon','cards','cactus','fountain','microphone','hotel','palm','wheel'],
  'mexico-city':['pyramids','flower','guitar','boat','cactus','arch','market','museum'],
  paris:['eiffel','arch','bakery','palette','balcony','coffee','books','bridge'],
  munich:['clock','pretzel','townhouse','garden','bicycle','theatre','museum','music'],
  moscow:['onion-dome','theatre','clock','arch','books','fountain','music','museum'],
  venice:['gondola','mask','bridge','balcony','lantern','sailboat','museum','palette'],
  beijing:['pagoda','gate','lantern','teapot','lotus','scroll','garden','museum'],
  seoul:['pagoda','tower','flower','teapot','music','bridge','market','bicycle'],
  tokyo:['torii','tower','flower','pagoda','teapot','tram','books','lantern'],
  'sao-paulo':['skyscraper','guitar','coffee','palette','palm','market','museum','flower'],
  'new-delhi':['dome','gate','lotus','minaret','teapot','garden','market','arch'],
  jakarta:['boat','tower','palm','flower','market','coffee','sailboat','museum'],
  hanoi:['pagoda','lotus','bicycle','lantern','boat','teapot','market','bridge'],
  klavius:space,
  solara:['solar','rover','rocket','crystal','biosphere',...space],
  atlantis:['shell','coral','dolphin','anchor','fish','trident','lighthouse','wave','pearl','sailboat','palace','turtle','compass','amphora','bridge','museum','fountain','scales','scroll','astrolabe','gate','garden'],
  cairo:cairoMotifs,
  istanbul:['dome','minaret','coffee-pot','lantern','bridge','boat','flower','market'],
  barcelona:['mosaic','tower','balcony','guitar','sailboat','palette','market','arch'],
}
export const propertyArtMotifs=(world:ReadyWorldId)=>[...new Set([...themes[world],...urban])].slice(0,22)
const palettes:Partial<Record<ReadyWorldId,readonly [string,string,string,string]>>={
  'new-york':['#ab692f','#286f89','#d7ac77','#fff0ce'],
  'las-vegas':['#be7620','#973e72','#edc575','#fff0ce'],
  'mexico-city':['#b54e2d','#267d69','#e4b76d','#fff0ce'],
  paris:['#af7d39','#436e8a','#dfc79c','#fff1d9'],
  munich:['#95602d','#386b98','#dcbc82','#fff2cf'],
  moscow:['#aa572d','#398263','#dcbe76','#fff0cc'],
  venice:['#ac572f','#307c83','#e1b984','#fff1cd'],
  beijing:['#b27b21','#963f35','#e0bb73','#fff0c9'],
  seoul:['#a76639','#317f73','#dfb976','#fff0ce'],
  tokyo:['#b45540','#465e88','#e7b6a1','#fff0d9'],
  'sao-paulo':['#a97522','#348064','#dfba65','#fff0c9'],
  'new-delhi':['#b77a23','#a34442','#e6b276','#fff0ce'],
  jakarta:['#a36729','#337d6b','#dcbd7e','#fff0cf'],
  hanoi:['#a36e24','#397765','#dbb46c','#fff1cc'],
  klavius:['#9b783d','#326b91','#aebac4','#edf3e9'],
  solara:['#b36332','#307877','#dbaa7d','#f4e9c9'],
  atlantis:['#a77b35','#277f8e','#bdcbb5','#e7efe0'],
  istanbul:['#aa7128','#2b7980','#dbb67b','#fff0cc'],
  barcelona:['#ae6531','#347e8a','#e4b680','#fff0cc'],
}

export function drawPropertyTileArt(c:CanvasRenderingContext2D,world:ReadyWorldId,position:number){
  const index=propertyArtPositions.indexOf(position as typeof propertyArtPositions[number])
  if(index<0)return
  if(world==='cairo'){drawCairoPropertyArt(c,position);return}
  const motif=propertyArtMotifs(world)[index],palette=palettes[world]??['#b77524','#267b76','#e4ba72','#fff0c9'] as const
  const cairoIndex=(cairoMotifs as readonly string[]).indexOf(motif)
  if(cairoIndex>=0){drawCairoPropertyArt(c,propertyArtPositions[cairoIndex],palette);return}
  const [gold,teal,sand,paper]=palette,ink='#071714'
  c.save();c.translate(256,365);c.scale(1.4,1.4);c.strokeStyle=ink;c.lineWidth=5;c.lineJoin='round';c.lineCap='round'
  const path=(d:string,fill?:string)=>{const p=new Path2D(d);if(fill){c.fillStyle=fill;c.fill(p)}c.stroke(p)}
  const rect=(x:number,y:number,w:number,h:number,fill:string)=>{c.fillStyle=fill;c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h)}
  const oval=(x:number,y:number,rx:number,ry:number,fill:string)=>{c.beginPath();c.ellipse(x,y,rx,ry,0,0,Math.PI*2);c.fillStyle=fill;c.fill();c.stroke()}
  const star=(x:number,y:number)=>path(`M${x} ${y-13} l4 9 10 4 -10 4 -4 10 -4 -10 -10 -4 10 -4 Z`,gold)
  const plant=()=>{path('M0 55 V-12');path('M0 9 Q-55 5 -43 -35 Q-3 -38 0 9 M0 -9 Q50 -11 40 -54 Q0 -47 0 -9',teal)}
  switch(motif){
    case 'townhouse':
      path('M-60 -35 L0 -78 L60 -35 Z',teal);rect(-50,-34,100,104,sand)
      for(const x of [-29,12])for(const y of [-17,17])rect(x,y,17,20,teal)
      path('M-13 69 V48 Q0 28 13 48 V69',gold);break
    case 'market':
      rect(-65,-13,130,78,sand);path('M-78 -14 L-60 -60 H60 L78 -14 Z',gold)
      for(const x of [-48,-16,16,48])path(`M${x-8} -57 L${x-13} -15 H${x+13} L${x+8} -57 Z`,paper)
      rect(-47,7,94,39,teal);oval(-29,51,9,9,gold);oval(0,51,9,9,gold);oval(29,51,9,9,gold);break
    case 'skyscraper':
      path('M-42 73 V-31 H-25 V-59 H-11 V-84 H11 V-59 H25 V-31 H42 V73 Z',sand)
      path('M0 -84 V-99 M-64 73 H64');for(const x of [-23,0,23])path(`M${x} -12 V56`);path('M-39 12 H39 M-39 37 H39');break
    case 'tree':
      path('M0 72 V-18 M0 35 L-32 4 M0 17 L26 -10',gold)
      oval(-29,-20,32,32,teal);oval(27,-27,33,32,teal);oval(0,-53,33,31,teal);path('M-48 73 H48');break
    case 'hotel':
      rect(-64,-38,128,109,sand);path('M-71 -38 L0 -72 L71 -38 Z',teal)
      for(const x of [-42,-9,24])for(const y of [-19,13])rect(x,y,18,18,teal)
      rect(-14,45,28,26,gold);star(0,-54);break
    case 'music':
      path('M-32 41 V-52 L48 -71 V19 M-32 -30 L48 -49',teal)
      oval(-48,43,23,16,gold);oval(32,23,23,16,gold);break
    case 'books':
      path('M-72 -47 Q-35 -69 0 -45 Q35 -69 72 -47 V53 Q33 36 0 59 Q-33 36 -72 53 Z',paper)
      path('M0 -45 V59 M-54 -29 L-17 -25 M17 -25 L54 -29 M-54 -6 L-17 -2 M17 -2 L54 -6 M-54 17 L-17 21 M17 21 L54 17');break
    case 'clock':
      rect(-32,-14,64,88,sand);path('M-43 -41 L0 -86 L43 -41 Z',teal)
      oval(0,-15,37,37,paper);path('M0 -39 V-15 L19 -3 M-43 74 H43');break
    case 'flower':
      path('M0 69 V-5 M0 42 Q-42 11 -47 42 Q-28 57 0 42 M0 27 Q39 0 45 29 Q29 44 0 27',teal)
      for(let i=0;i<6;i++){const a=i*Math.PI/3;oval(Math.cos(a)*27,-32+Math.sin(a)*27,18,18,sand)}oval(0,-32,17,17,gold);break
    case 'bicycle':
      oval(-50,35,30,30,paper);oval(50,35,30,30,paper)
      path('M-50 35 L-21 -22 L11 35 Z M-21 -22 H30 L11 35 M50 35 L26 -43 H42 M-35 -29 H-12');break
    case 'arch':
      path('M-66 71 V-52 H66 V71 H25 V2 Q0 -35 -25 2 V71 Z',sand)
      path('M-72 -52 H72 V-70 H-72 Z',gold);path('M-50 -36 H50 M-52 -16 V48 M52 -16 V48');break
    case 'bakery':
      path('M-79 29 Q-96 -8 -59 -35 Q-62 -63 -28 -57 Q0 -83 28 -57 Q62 -63 59 -35 Q96 -8 79 29 L45 2 Q0 39 -45 2 Z',gold)
      path('M-29 -55 L-18 16 M29 -55 L18 16 M-58 -34 L-45 2 M58 -34 L45 2');break
    case 'garden':
      rect(-75,31,150,39,sand);plant();path('M-43 30 V-4 M43 30 V-4')
      oval(-43,-12,19,19,gold);oval(43,-12,19,19,gold);break
    case 'statue':
      path('M-43 74 H43 V52 H-43 Z',sand);path('M-27 52 L-14 -30 H13 L33 52 Z',teal)
      oval(0,-47,14,16,teal);path('M-12 -29 L-39 -55 L-44 -84 M12 -20 L39 5 L28 24',teal)
      path('M-52 -85 Q-61 -110 -41 -106 Q-25 -109 -35 -85 Z',gold);path('M-21 -56 L-26 -71 M-9 -63 L-9 -78 M7 -62 L12 -78 M20 -56 L31 -68');break
    case 'taxi':
      path('M-78 36 V-2 L-48 -13 L-29 -49 H32 L55 -10 L78 3 V36 Z',gold)
      path('M-38 -13 L-23 -39 H23 L43 -13 Z',teal);rect(-16,-62,32,13,paper)
      oval(-47,39,17,17,ink);oval(47,39,17,17,ink);path('M-68 9 H-43 M45 9 H69');break
    case 'gallery':
      rect(-72,-67,144,127,gold);rect(-57,-52,114,97,paper)
      path('M-53 41 L-20 -11 L6 17 L30 -27 L53 42 Z',teal);oval(-26,-28,12,12,gold);break
    case 'neon':
      path('M-85 -25 L-48 -54 H48 L85 -25 L48 28 H-48 Z',gold)
      path('M-61 -22 L-35 -37 H35 L61 -22 L35 12 H-35 Z',teal);star(0,-12);path('M-24 29 V73 M24 29 V73');star(0,-77);break
    case 'cards':
      path('M-69 -55 L-9 -73 L24 46 L-37 65 Z',teal);path('M-13 -65 L63 -50 L40 74 L-35 59 Z',paper)
      path('M17 -20 L37 8 L9 32 L-8 4 Z',gold);break
    case 'cactus':
      path('M-15 71 V-8 H-40 Q-68 -8 -68 -36 V-55 Q-55 -70 -43 -55 V-36 H-15 V-64 Q0 -88 15 -64 V3 H43 V-26 Q57 -43 70 -26 V3 Q70 29 43 29 H15 V71 Z',teal)
      path('M-38 73 H38 M0 -56 V56');break
    case 'microphone':
      path('M-25 -60 Q0 -84 25 -60 V-13 Q0 14 -25 -13 Z',gold)
      path('M-42 -22 V-10 Q0 43 42 -10 V-22 M0 29 V70 M-35 71 H35 M-17 -48 H17 M-17 -31 H17');break
    case 'wheel':
      oval(0,-6,57,57,paper);for(let i=0;i<8;i++){const a=i*Math.PI/4,x=Math.cos(a)*57,y=-6+Math.sin(a)*57;path(`M0 -6 L${x} ${y}`);oval(x,y,10,10,gold)}
      path('M0 -6 L-35 77 H35 Z',teal);break
    case 'guitar':
      path('M-7 -21 Q-40 -50 -53 -19 Q-26 -8 -48 9 Q-77 40 -37 66 Q0 86 14 51 Q22 29 11 13 L60 -55 L45 -66 Z',gold)
      oval(-18,23,13,13,ink);path('M-33 46 L-7 58 M-21 29 L52 -60');break
    case 'boat':
      path('M-85 24 H85 L54 60 H-51 Z',teal);path('M-51 20 V-37 H50 V20 M-72 -36 Q0 -92 72 -36 Z',gold)
      path('M-23 -35 V18 M23 -35 V18 M-68 75 Q-36 58 -4 75 T60 75');break
    case 'eiffel':
      path('M-66 74 L-25 -4 L-10 -75 H10 L25 -4 L66 74 H29 Q0 10 -29 74 Z',gold)
      path('M0 -76 V-100 M-23 -5 H23 M-40 33 H40 M-13 -48 H13 M-17 -35 L23 0 M17 -35 L-23 0');break
    case 'pretzel':
      path('M-43 29 Q-103 -14 -59 -53 Q-22 -78 0 -15 Q22 -78 59 -53 Q103 -14 43 29 L-14 67 Q-40 76 -42 54 Q-38 33 -12 22 L48 -13 M-48 -13 L12 22 Q38 33 42 54 Q40 76 14 67 Z',gold)
      path('M-59 -29 L-50 -34 M49 -34 L58 -29 M-18 47 L-12 54 M17 47 L11 54');break
    case 'onion-dome':
      rect(-39,9,78,63,sand);path('M-41 7 Q-71 -24 -23 -51 Q0 -65 0 -86 Q0 -65 23 -51 Q71 -24 41 7 Z',teal)
      path('M0 -88 V-105 M-10 -98 H10 M0 -57 Q-30 -19 -16 6 M12 -49 Q39 -12 25 6 M-13 70 V38 Q0 16 13 38 V70');break
    case 'gondola':
      path('M-91 7 Q0 58 83 -9 L96 -27 L93 13 Q0 96 -80 40 Z',teal)
      path('M-44 18 V-13 Q-4 -49 28 -13 V23',gold);path('M36 -57 L-26 69 M-39 70 L-17 80');break
    case 'mask':
      path('M-81 -38 Q-45 -65 0 -36 Q45 -65 81 -38 L64 16 Q34 54 0 69 Q-34 54 -64 16 Z',gold)
      path('M-59 -21 Q-28 -38 -13 -11 Q-41 6 -59 -21 Z M13 -11 Q28 -38 59 -21 Q41 6 13 -11 Z',teal)
      path('M0 -25 L-8 17 H8 M-21 34 Q0 47 21 34');break
    case 'pagoda':
      rect(-43,28,86,44,sand);rect(-32,-18,64,40,sand);rect(-19,-54,38,31,sand)
      for(const [y,w] of [[-55,43],[-19,61],[27,81]])path(`M${-w} ${y-8} Q0 ${y+7} ${w} ${y-8} L${w-12} ${y+8} H${-w+12} Z`,teal)
      path('M0 -55 V-87 M-12 71 V49 H12 V71');break
    case 'teapot':
      oval(-4,16,48,40,teal);path('M-46 0 L-78 -26 L-82 -12 L-51 27',gold)
      path('M38 -7 Q92 -26 76 26 Q65 40 42 27');path('M-31 -21 Q0 -48 29 -21 Z',gold);oval(0,-46,7,7,gold);break
    case 'tower':
      path('M-25 75 L-12 -11 H12 L25 75 Z',sand);oval(0,-24,40,15,teal)
      path('M-29 -24 V-44 H29 V-24 M0 -45 V-95 M-45 75 H45 M-11 13 H11 M-17 47 H17');break
    case 'torii':
      path('M-80 -65 Q0 -40 80 -65 L70 -45 Q0 -24 -70 -45 Z',gold)
      rect(-52,-40,16,110,teal);rect(36,-40,16,110,teal);rect(-72,-15,144,14,gold);break
    case 'mosaic':
      path('M-67 -65 H67 V67 H-67 Z',gold);path('M0 -55 L55 0 L0 55 L-55 0 Z',teal)
      path('M0 -35 L10 -10 L35 0 L10 10 L0 35 L-10 10 L-35 0 L-10 -10 Z',paper)
      path('M-56 -51 L-25 -51 M25 -51 H56 M-56 51 H-25 M25 51 H56');break
    case 'rocket':
      path('M-23 29 V-20 Q-22 -60 0 -86 Q22 -60 23 -20 V29 Z',paper)
      path('M-23 -3 L-48 26 V56 L-23 35 M23 -3 L48 26 V56 L23 35',teal)
      oval(0,-24,13,13,teal);path('M-15 36 L0 79 L15 36 Z',gold);break
    case 'telescope':
      path('M-62 -47 L-42 -73 L57 2 L38 29 Z',sand);oval(-52,-60,16,13,teal)
      path('M0 -5 V66 M0 19 L-45 71 M0 19 L45 71');star(66,-57);break
    case 'biosphere':
      path('M-77 37 Q-75 -75 0 -77 Q75 -75 77 37 Z',paper)
      path('M-77 37 H77 V55 H-77 Z',gold);path('M-47 34 Q-45 -41 0 -70 Q45 -41 47 34 M-65 -5 H65');plant();break
    case 'rover':
      path('M-72 30 V-10 H-38 V-45 H21 L48 -10 H72 V30 Z',sand)
      path('M-26 -35 H15 L31 -12 H-26 Z',teal);path('M50 -10 V-70 M40 -70 H62')
      for(const x of [-48,0,48])oval(x,36,18,18,ink);break
    case 'satellite':
      rect(-22,-27,44,55,gold);rect(-84,-41,52,73,teal);rect(32,-41,52,73,teal)
      path('M-58 -39 V30 M58 -39 V30 M-83 -4 H-33 M33 -4 H83 M0 -28 V-66 M-17 -75 Q0 -49 17 -75 M0 29 V58');break
    case 'solar':
      path('M-65 -15 H65 L84 48 H-84 Z',teal);path('M-42 -14 L-53 47 M0 -14 V47 M42 -14 L53 47 M-75 16 H75 M0 49 V73 M-26 74 H26')
      oval(0,-60,20,20,gold);for(const [x,y] of [[-35,-60],[35,-60],[0,-95]])path(`M${x} ${y} l${x?Math.sign(x)*9:0} ${x?0:-9}`);break
    case 'habitat':
      path('M-73 59 V-6 Q-72 -54 -26 -54 H29 Q74 -54 74 -6 V59 Z',sand)
      rect(-51,-24,37,26,teal);rect(14,-24,37,26,teal);path('M-14 58 V22 Q0 2 14 22 V58 M-73 20 H-29 M29 20 H73');break
    case 'antenna':
      path('M-65 -59 Q-49 13 30 5 Z',paper);path('M-21 -23 L35 -75 M21 -3 L0 64 M-35 69 H35');oval(37,-78,7,7,gold)
      path('M56 -70 Q76 -49 59 -31 M70 -86 Q104 -50 79 -16');break
    case 'laboratory':
      path('M-24 -72 H24 V-23 L65 50 Q72 69 48 73 H-48 Q-72 69 -65 50 L-24 -23 Z',paper)
      path('M-38 20 H38 L61 59 H-61 Z',teal);path('M-32 -72 H32');oval(-12,1,7,7,gold);oval(13,-22,7,7,gold);break
    case 'airlock':
      path('M-57 -71 H57 L75 -51 V51 L57 71 H-57 L-75 51 V-51 Z',sand)
      rect(-48,-45,96,89,teal);oval(0,0,27,27,paper);path('M-17 0 H17 M0 -17 V17 M-33 -58 H33');break
    case 'oxygen':
      path('M-62 56 V-38 Q-62 -61 -40 -61 Q-18 -61 -18 -38 V56 Z M15 56 V-38 Q15 -61 37 -61 Q59 -61 59 -38 V56 Z',teal)
      path('M-40 -61 V-77 M37 -61 V-77 M-51 -77 H-29 M26 -77 H48 M-61 -6 H-19 M16 -6 H58');break
    case 'lander':
      path('M-37 -43 L0 -69 L37 -43 V11 H-37 Z',gold);rect(-20,-36,40,25,teal)
      path('M-37 0 L-62 52 H-83 M37 0 L62 52 H83 M-21 11 L-31 34 H31 L21 11 Z',sand);path('M-17 42 L0 75 L17 42',gold);break
    case 'orbit':
      oval(0,0,36,36,teal);c.save();c.rotate(-.5);oval(0,0,85,27,'#00000000');c.restore();oval(69,-38,14,14,gold);star(-65,-64);break
    case 'radar':
      oval(0,0,68,68,teal);oval(0,0,43,43,teal);oval(0,0,20,20,teal)
      path('M0 -65 V65 M-65 0 H65 M0 0 L45 -45');oval(33,21,7,7,gold);oval(-18,-39,7,7,gold);break
    case 'crystal':
      path('M-19 68 L-42 -18 L-16 -74 L17 -51 L39 13 L19 68 Z',teal)
      path('M-42 -18 L-4 -7 L17 -51 M-4 -7 L-19 68 M39 13 L-4 -7 M-39 64 L-77 16 L-62 -21 L-42 -18 M20 68 L76 18 L63 -20 L39 13',sand);break
    case 'crater':
      oval(0,19,85,44,sand);oval(0,4,60,31,gold);oval(0,10,35,17,teal)
      path('M-79 17 L-62 0 M-38 -14 L-30 -5 M41 -12 L31 0 M75 34 L55 24');star(58,-63);break
    case 'astronaut':
      oval(0,-42,36,35,paper);oval(0,-43,27,20,teal);rect(-27,-4,54,55,sand)
      path('M-26 2 L-49 28 M26 2 L49 28 M-16 52 V79 M16 52 V79');rect(-13,10,26,21,teal);break
    case 'battery':
      rect(-46,-59,92,132,teal);rect(-20,-76,40,17,gold)
      path('M9 -43 L-23 7 H-1 L-12 50 L29 -6 H8 Z',gold);break
    case 'greenhouse':
      path('M-72 71 V-20 L0 -78 L72 -20 V71 Z',paper)
      path('M-72 -20 H72 M0 -78 V-20 M-72 70 H72');plant();break
    case 'cargo':
      path('M-69 -39 L0 -71 L69 -39 V43 L0 77 L-69 43 Z',sand)
      path('M-69 -39 L0 -6 L69 -39 M0 -6 V77 M-24 -60 L45 -27 V10 L24 19 V-16 L-45 -49',teal);break
    case 'beacon':
      path('M-30 74 L-17 -17 H17 L30 74 Z',sand);oval(0,-29,27,27,gold)
      path('M-46 -50 Q-64 -28 -46 -7 M46 -50 Q64 -28 46 -7 M-65 -68 Q-96 -29 -65 11 M65 -68 Q96 -29 65 11 M-43 76 H43');break
    case 'planet':
      oval(0,0,60,60,world==='solara'?gold:paper);oval(-20,-17,15,12,sand);oval(25,23,19,14,teal);oval(22,-29,8,8,gold);star(-69,-67);star(74,57);break
    case 'shell':
      path('M-17 64 Q-89 2 -73 -43 Q-48 -74 -21 -57 Q0 -90 21 -57 Q48 -74 73 -43 Q89 2 17 64 Z',paper)
      path('M0 59 V-61 M-7 59 L-47 -51 M7 59 L47 -51 M-13 58 L-71 -22 M13 58 L71 -22');break
    case 'coral':
      path('M-14 71 V25 L-60 -8 V-47 M-60 -7 L-83 -27 M-14 25 V-47 L-31 -70 M-14 -31 L4 -60 M14 71 V13 L55 -15 V-61 M54 -16 L79 -32 M14 13 V-16',teal)
      for(const [x,y] of [[-60,-49],[-83,-29],[-31,-72],[4,-62],[55,-63],[79,-34]])oval(x,y,8,8,gold);break
    case 'dolphin':
      path('M-80 38 Q-40 -65 31 -36 L64 -13 L87 -5 L61 5 Q32 4 6 20 L-21 53 L-26 23 Q-56 17 -63 45 L-57 64 L-80 55 L-96 62 Z',teal)
      path('M-13 -33 L-5 -65 L18 -37',teal);oval(44,-13,4,4,ink);break
    case 'anchor':
      oval(0,-55,19,19,gold);path('M0 -35 V56 M-32 -15 H32 M-66 13 Q-68 57 0 71 Q68 57 66 13 M-80 29 L-65 10 L-46 28 M46 28 L65 10 L80 29');break
    case 'fish':
      path('M-76 0 Q-12 -69 48 -11 L83 -43 V43 L48 11 Q-12 69 -76 0 Z',teal)
      oval(-41,-5,7,7,paper);path('M-18 -31 Q-39 0 -18 31 M3 -22 L25 0 L3 22');break
    case 'trident':
      path('M0 78 V-74 M-61 -42 V-3 Q0 55 61 -3 V-42 M-16 -56 L0 -81 L16 -56 M-76 -23 L-61 -48 L-45 -23 M45 -23 L61 -48 L76 -23');break
    case 'lighthouse':
      path('M-37 71 L-22 -42 H22 L37 71 Z',paper);rect(-29,-62,58,24,gold)
      path('M-38 -63 L0 -88 L38 -63 Z',teal);path('M-27 -16 H27 M-33 30 H33 M-43 -51 L-85 -66 M43 -51 L85 -66');break
    case 'wave':
      path('M-86 55 Q-38 30 -27 -17 Q-15 -76 40 -58 Q74 -43 61 -12 Q44 -41 25 -21 Q8 -3 33 27 Q48 45 87 49 L71 72 H-86 Z',teal)
      path('M-50 52 Q-17 39 -6 3 M34 43 Q54 58 74 59');break
    case 'pearl':
      path('M-73 25 Q-92 -49 -51 -67 Q0 -79 51 -67 Q92 -49 73 25 Z',sand)
      path('M-73 26 Q0 -3 73 26 Q50 80 0 77 Q-50 80 -73 26 Z',teal);oval(0,15,32,32,paper);break
    case 'palace':
      rect(-75,-26,150,96,sand);rect(-36,-56,72,126,gold);path('M-43 -57 L0 -88 L43 -57 Z',teal)
      for(const x of [-57,57])path(`M${x-11} 48 V-3 Q${x} -23 ${x+11} -3 V48`,teal)
      path('M-15 70 V15 Q0 -7 15 15 V70',paper);break
    case 'turtle':
      for(const [x,y] of [[-48,-41],[48,-41],[-48,41],[48,41]])oval(x,y,22,14,teal)
      oval(0,-68,15,21,teal);oval(0,0,48,59,gold);path('M0 -35 L29 -17 L29 20 L0 37 L-29 20 L-29 -17 Z',teal);break
    case 'compass':
      oval(0,0,66,66,gold);oval(0,0,51,51,paper);path('M0 -43 L13 -8 L43 0 L8 13 L0 43 L-13 8 L-43 0 L-8 -13 Z',teal);break
    case 'amphora':
      path('M-20 -61 H20 V-26 Q64 4 38 56 Q0 91 -38 56 Q-64 4 -20 -26 Z',sand)
      path('M-24 -20 Q-84 -49 -70 6 Q-60 29 -43 23 M24 -20 Q84 -49 70 6 Q60 29 43 23 M-27 -64 H27 M-43 15 H43 M-39 35 H39');break
  }
  c.restore()
}
